import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { fmtAUD } from "../lib/format";
import { Upload, Link as LinkIcon, X, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, TrendingDown, TrendingUp, Minus, Scale } from "lucide-react";


// ============================================================
// Claim Value Banner — shows the lifecycle of the council's claim
// ============================================================

export function ClaimValueBanner({ job }) {
  const submissionId = job?.submission_id;
  const [nswra, setNswra] = useState(null);
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/submissions/${submissionId}/nswra-compile`);
        if (!cancelled) setNswra(r.data);
      } catch (e) { /* silent — banner just hides the row */ }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  if (!job) return null;
  const epar = job.epar_assessment || {};
  const epa = job.epa_rw_rollup || {};
  const evid = job.evidentiary_cet || null;

  const form1 = epar.total_submitted_erc || 0;
  const rfiUpdate = epa.rfi_update_total || 0;
  const evidTotal = evid ? (evid.preview?.escalated_p50 || evid.preview?.base_total || 0) : 0;

  // Nothing useful to show
  if (!form1 && !rfiUpdate && !evidTotal && !nswra?.available) return null;

  // Effective (current) claim — highest-priority source wins.
  const effective = evidTotal || rfiUpdate || form1;
  const effectiveLabel = evidTotal ? "RHE Evidentiary CET" : rfiUpdate ? "RFI revision" : "Form 1 lodgement";

  const fmt = (v) => v ? `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
  const stages = [];
  if (form1) stages.push({ label: "Form 1 lodgement", value: form1, source: "OneGMS Form 1 H4 (=AF70)" });
  if (rfiUpdate) {
    const delta = form1 ? ((rfiUpdate - form1) / form1) * 100 : null;
    stages.push({
      label: "Council RFI revision",
      value: rfiUpdate,
      source: `${epa.rfi_update_workbook_count || 1} Scope Sheet workbook${(epa.rfi_update_workbook_count || 1) === 1 ? "" : "s"}`,
      delta,
      kind: rfiUpdate < form1 ? "down" : rfiUpdate > form1 ? "up" : "flat",
    });
  }
  if (evidTotal) {
    const prev = rfiUpdate || form1;
    const delta = prev ? ((evidTotal - prev) / prev) * 100 : null;
    stages.push({
      label: "RHE Evidentiary CET (published)",
      value: evidTotal,
      source: "Official NSWRA v2.1 template, audit-grade",
      delta,
      kind: evidTotal < prev ? "down" : evidTotal > prev ? "up" : "flat",
    });
  }

  return (
    <div className="mb-5 border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="claim-value-banner">
      <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">Council&apos;s current claim</div>
          <div className="font-mono text-2xl tabular font-bold mt-0.5">{fmt(effective)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">Source</div>
          <div className="text-sm font-semibold mt-0.5">{effectiveLabel}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        {stages.map((s) => (
          <div key={s.label} className={`px-4 py-3 ${s.value === effective ? "bg-emerald-50/40" : "bg-white"}`} data-testid={`claim-stage-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">{s.label}</div>
              {s.value === effective && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold tracking-wider uppercase">Current</span>}
            </div>
            <div className="font-mono text-lg tabular text-slate-800">{fmt(s.value)}</div>
            {s.delta != null && (
              <div className={`text-[11px] mt-1 flex items-center gap-1 ${s.kind === "down" ? "text-red-700" : s.kind === "up" ? "text-amber-700" : "text-slate-500"}`}>
                {s.kind === "down" ? <TrendingDown className="w-3 h-3"/> : s.kind === "up" ? <TrendingUp className="w-3 h-3"/> : <Minus className="w-3 h-3"/>}
                <span>{s.delta > 0 ? "+" : ""}{s.delta.toFixed(1)}% vs prior stage</span>
              </div>
            )}
            <div className="text-[10px] text-slate-400 mt-1">{s.source}</div>
          </div>
        ))}
      </div>

      {/* NSWRA-canonical RHE compile vs council comparison */}
      {nswra?.available && (
        <NSWRACompileRow nswra={nswra} councilTotal={effective} submissionId={submissionId} />
      )}
      <Form2FinaliseRow submissionId={submissionId} job={job} nswra={nswra} councilTotal={effective} />
    </div>
  );
}


function NSWRACompileRow({ nswra, councilTotal, submissionId }) {
  const fmt = (v) => v == null ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const t = nswra.totals || {};
  const nswraP50 = t.nswra_total_p50 || 0;
  const nswraP90 = t.nswra_total_p90 || 0;
  const deltaP50 = nswra.delta_p50_pct;
  const deltaP90 = nswra.delta_p90_pct;
  const downloadUrl = `${API}/submissions/${submissionId}/nswra-compile/download`;
  const councilHigherP50 = (deltaP50 || 0) > 0;
  const inferenceText = councilHigherP50
    ? `Council is claiming MORE than NSWRA's P50 estimate — flag for assessor review.`
    : `Council is claiming LESS than NSWRA's P50 estimate (conservative). NSWRA P90 Damage Estimate = official funding-eligible cap.`;

  return (
    <div className="border-t-2 border-blue-300 bg-blue-50/40 px-5 py-3" data-testid="nswra-compile-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-blue-700"/>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-blue-800">RHE NSWRA-canonical CET</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {nswra.region} · {nswra.fy} · {nswra.line_item_count} lines → {nswra.aggregated_ref_count} NSWRA refs · Excel-evaluated
              {nswra.unmatched_treatments?.length > 0 && (
                <span className="text-amber-700 ml-2">⚠ {nswra.unmatched_treatments.length} unmatched</span>
              )}
            </div>
          </div>
        </div>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          data-testid="nswra-compile-download-link"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-blue-300 bg-white text-blue-800 text-[11px] tracking-wider uppercase font-bold hover:bg-blue-50"
        >
          <FileSpreadsheet className="w-3.5 h-3.5"/> Download .xlsx
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
        <div className="p-2 rounded border border-blue-200 bg-white">
          <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-slate-500">Council RFI claim</div>
          <div className="font-mono text-base tabular text-slate-700">{fmt(councilTotal || t.council_total)}</div>
        </div>
        <div className="p-2 rounded border border-blue-300 bg-blue-50">
          <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-blue-800">NSWRA P50 (contracting basis)</div>
          <div className="font-mono text-base tabular text-blue-900 font-bold">{fmt(nswraP50)}</div>
          {deltaP50 != null && (
            <div className={`text-[10px] mt-0.5 ${councilHigherP50 ? "text-amber-700" : "text-emerald-700"}`}>
              Council {councilHigherP50 ? "+" : ""}{deltaP50}% vs NSWRA P50
            </div>
          )}
        </div>
        <div className="p-2 rounded border border-blue-300 bg-blue-50">
          <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-blue-800">NSWRA P90 (Damage Estimate)</div>
          <div className="font-mono text-base tabular text-blue-900 font-bold">{fmt(nswraP90)}</div>
          {deltaP90 != null && (
            <div className="text-[10px] mt-0.5 text-emerald-700">
              Council {deltaP90 > 0 ? "+" : ""}{deltaP90}% vs NSWRA P90
            </div>
          )}
        </div>
      </div>
      <div className="text-[11px] text-slate-700 mt-2">{inferenceText}</div>

      {/* Per-treatment breakdown table */}
      {nswra.by_ref?.length > 0 && (
        <details className="mt-2">
          <summary className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-blue-700 cursor-pointer hover:text-blue-900">
            Show per-treatment breakdown ({nswra.by_ref.length} refs)
          </summary>
          <table className="w-full text-[11.5px] mt-2 bg-white rounded border border-blue-100" data-testid="nswra-compile-breakdown">
            <thead>
              <tr className="bg-blue-50 text-left text-[10px] tracking-[0.14em] uppercase text-slate-600 border-b border-blue-200">
                <th className="px-2 py-1">Ref</th>
                <th className="px-2 py-1">Treatment</th>
                <th className="px-2 py-1 text-right">Qty</th>
                <th className="px-2 py-1 text-right">NSWRA rate</th>
                <th className="px-2 py-1 text-right">NSWRA direct</th>
                <th className="px-2 py-1 text-right">Council direct</th>
                <th className="px-2 py-1 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {nswra.by_ref.map(r => {
                const delta = r.nswra_direct ? (r.council_direct - r.nswra_direct) / r.nswra_direct * 100 : 0;
                return (
                  <tr key={r.ref} className="border-b border-blue-50">
                    <td className="px-2 py-1 font-mono text-[10.5px]">{r.ref}</td>
                    <td className="px-2 py-1">{r.treatment}</td>
                    <td className="px-2 py-1 text-right font-mono">{r.qty.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right font-mono">${r.nswra_rate.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-mono text-blue-900">{fmt(r.nswra_direct)}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-600">{fmt(r.council_direct)}</td>
                    <td className={`px-2 py-1 text-right font-mono text-[10.5px] ${delta > 5 ? "text-amber-700" : delta < -5 ? "text-emerald-700" : "text-slate-400"}`}>
                      {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}


// ============================================================
// Form 2 Finalisation Row — one-click write-back + .xlsx/PDF buttons
// ============================================================

function Form2FinaliseRow({ submissionId, job, nswra, councilTotal }) {
  const [finalisation, setFinalisation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);

  // Hydrate from the existing submission record if finalise already ran
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/submissions/${submissionId}`);
        if (!cancelled && r.data?.form2_finalisation) {
          setFinalisation(r.data.form2_finalisation);
        }
      } catch (e) { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  if (!submissionId || !nswra?.available) return null;

  const togglePreview = async () => {
    if (previewOpen) { setPreviewOpen(false); return; }
    setPreviewOpen(true);
    if (preview) return;
    setPreviewLoading(true); setPreviewErr(null);
    try {
      const r = await axios.get(`${API}/submissions/${submissionId}/finalise-form2/preview`);
      setPreview(r.data);
    } catch (e) {
      setPreviewErr(e.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${API}/submissions/${submissionId}/finalise-form2`, {
        assessor_name: "",
        assessor_email: "",
      });
      setFinalisation(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Finalise failed");
    } finally {
      setBusy(false);
    }
  };

  const fmt = (v) => v == null ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const downloadUrl = `${API}/submissions/${submissionId}/finalise-form2/download`;
  const reportUrl = `${API}/submissions/${submissionId}/assessment-report.pdf`;
  const isDone = !!finalisation;
  const accepted = finalisation?.accepted_total;
  const basis = finalisation?.accepted_basis;

  return (
    <div className="border-t-2 border-emerald-300 bg-emerald-50/40 px-5 py-3" data-testid="form2-finalise-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-emerald-700"/>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-800">Finalised Form 2 Workbook</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {isDone
                ? `Accepted ${fmt(accepted)} · basis: ${basis === "council" ? "council RFI (lower-of)" : "NSWRA P90"} · ${finalisation.rows_written || finalisation.rows_written?.length || 0} rows · BG70=H12=Summary!C22`
                : "Click to stamp NSWRA-eligible BG values + assessor narrative into Form 2"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={togglePreview}
            data-testid="form2-preview-bl-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-800 text-[11px] tracking-wider uppercase font-bold hover:bg-emerald-50"
            title="Preview the Form 2 BL Assessor notes column"
          >
            {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Scale className="w-3.5 h-3.5"/>}
            {previewOpen ? "Hide BL preview" : "Preview BL column"}
          </button>
          {!isDone && (
            <button
              type="button"
              onClick={run}
              disabled={busy}
              data-testid="form2-finalise-run-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-400 bg-emerald-600 text-white text-[11px] tracking-wider uppercase font-bold hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <CheckCircle2 className="w-3.5 h-3.5"/>}
              {busy ? "Finalising…" : "Finalise Form 2"}
            </button>
          )}
          {isDone && (
            <>
              <a
                href={reportUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="form2-assessment-report-pdf-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-800 text-[11px] tracking-wider uppercase font-bold hover:bg-emerald-50"
              >
                EPAR Report PDF
              </a>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="form2-download-xlsx-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-800 text-[11px] tracking-wider uppercase font-bold hover:bg-emerald-50"
              >
                <FileSpreadsheet className="w-3.5 h-3.5"/> Download .xlsx
              </a>
              <button
                type="button"
                onClick={run}
                disabled={busy}
                data-testid="form2-finalise-rerun-btn"
                title="Re-run finalisation (overwrites the workbook)"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-700 text-[10px] tracking-wider uppercase font-bold hover:bg-emerald-50 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : "Re-run"}
              </button>
            </>
          )}
        </div>
      </div>
      {err && (
        <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          <AlertTriangle className="inline w-3.5 h-3.5 mr-1"/> {err}
        </div>
      )}
      {isDone && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <div className="p-2 rounded border border-emerald-200 bg-white">
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-slate-500">Council RFI claim</div>
            <div className="font-mono text-base tabular text-slate-700">{fmt(finalisation.council_total)}</div>
          </div>
          <div className="p-2 rounded border border-blue-200 bg-blue-50/40">
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-blue-700">NSWRA P50</div>
            <div className="font-mono text-base tabular text-blue-900">{fmt(finalisation.headline_p50)}</div>
          </div>
          <div className="p-2 rounded border border-blue-200 bg-blue-50/40">
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-blue-700">NSWRA P90 (cap)</div>
            <div className="font-mono text-base tabular text-blue-900">{fmt(finalisation.headline_p90)}</div>
          </div>
          <div className="p-2 rounded border-2 border-emerald-400 bg-emerald-50">
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-bold text-emerald-800">Accepted (BG70 → H12)</div>
            <div className="font-mono text-base tabular text-emerald-900 font-bold">{fmt(accepted)}</div>
            <div className="text-[9.5px] mt-0.5 text-emerald-700">Lower-of · {basis}</div>
          </div>
        </div>
      )}
      {previewOpen && (
        <div className="mt-3 border border-emerald-200 rounded-md bg-white" data-testid="form2-bl-preview-panel">
          <div className="px-3 py-2 border-b border-emerald-200 bg-emerald-50/60 flex items-center justify-between">
            <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-emerald-800">
              Form 2 · Column BL Preview · &ldquo;Assessor notes/remarks&rdquo;
            </div>
            <div className="text-[10px] text-slate-500">Read-only · auto-generated · written by Finalise Form 2</div>
          </div>
          {previewLoading && (
            <div className="px-4 py-6 text-center text-[12px] text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2"/> Computing lower-of decision across every DM…
            </div>
          )}
          {previewErr && (
            <div className="m-3 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              <AlertTriangle className="inline w-3.5 h-3.5 mr-1"/> {previewErr}
            </div>
          )}
          {preview?.available && (
            <>
              {/* Headline ribbon + escalation banner */}
              <div className="px-3 py-2 border-b border-emerald-100 bg-white">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                  <div className="p-1.5 rounded border border-slate-200 bg-slate-50">
                    <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-slate-500">Form 1 (original)</div>
                    <div className="font-mono tabular text-slate-700">{fmt(preview.form1_original_total)}</div>
                  </div>
                  <div className="p-1.5 rounded border border-amber-200 bg-amber-50">
                    <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-amber-700">RFI revised ({preview.rfi_direction})</div>
                    <div className="font-mono tabular text-amber-900">{fmt(preview.council_total)}</div>
                    <div className="text-[9px] text-amber-700 mt-0.5">{preview.rfi_direction === "UNCHANGED" ? "no change" : `${preview.rfi_direction === "DOWN" ? "-" : "+"}${fmt(Math.abs(preview.rfi_delta_dollar))} (${Math.abs(preview.rfi_delta_pct).toFixed(2)}%)`}</div>
                  </div>
                  <div className="p-1.5 rounded border border-blue-200 bg-blue-50/40">
                    <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-blue-700">NSWRA CET ({(preview.cet_basis||"p90").toUpperCase()})</div>
                    <div className="font-mono tabular text-blue-900">{fmt(preview.cet_headline)}</div>
                  </div>
                  <div className="p-1.5 rounded border-2 border-emerald-400 bg-emerald-50">
                    <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-emerald-800">Accepted (lower-of)</div>
                    <div className="font-mono tabular text-emerald-900 font-bold">{fmt(preview.accepted_total)}</div>
                    <div className="text-[9px] text-emerald-700 mt-0.5">basis: {preview.accepted_basis === "council" ? "council RFI" : "NSWRA P90"}</div>
                  </div>
                  <div className={`p-1.5 rounded border-2 ${preview.needs_escalated_approval ? "border-red-400 bg-red-50" : "border-emerald-300 bg-emerald-50/50"}`}>
                    <div className={`text-[9px] tracking-[0.14em] uppercase font-bold ${preview.needs_escalated_approval ? "text-red-800" : "text-emerald-800"}`}>Delegation</div>
                    <div className={`text-[11px] font-bold ${preview.needs_escalated_approval ? "text-red-900" : "text-emerald-900"}`}>{preview.outcome_delta_pct.toFixed(2)}% vs council</div>
                    <div className={`text-[9px] mt-0.5 ${preview.needs_escalated_approval ? "text-red-700" : "text-emerald-700"}`}>{preview.needs_escalated_approval ? "Senior approval REQ." : "No escalation"}</div>
                  </div>
                </div>
                <div className={`mt-2 px-2 py-1.5 rounded text-[11px] leading-snug ${preview.needs_escalated_approval ? "bg-red-50 border border-red-200 text-red-900" : "bg-emerald-50 border border-emerald-200 text-emerald-900"}`}>
                  {preview.escalation_sentence}
                </div>
              </div>
              {/* Per-row BL table */}
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-[11px]" data-testid="form2-bl-preview-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-[10px] tracking-[0.12em] uppercase font-bold text-slate-600 border-b border-slate-200">
                      <th className="px-2 py-1.5">DM / Asset</th>
                      <th className="px-2 py-1.5">Ref</th>
                      <th className="px-2 py-1.5 text-right">Council BF</th>
                      <th className="px-2 py-1.5 text-right">NSWRA CET BG</th>
                      <th className="px-2 py-1.5 text-right">Accepted</th>
                      <th className="px-2 py-1.5">BL note (Assessor notes/remarks)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(preview.rows || []).map((r, idx) => {
                      const lgaLower = r.cet_total >= r.council_total;
                      return (
                        <tr key={idx} className="hover:bg-emerald-50/40" data-testid={`form2-bl-row-${idx}`}>
                          <td className="px-2 py-1.5">
                            <div className="font-mono text-[10.5px] text-slate-700">{r.dm_id || r.asset_id || "—"}</div>
                            <div className="text-[10px] text-slate-500 truncate max-w-[260px]">{r.asset_name || ""}</div>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[10.5px] text-slate-600">{r.final_ref || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular text-slate-700">{fmt(r.council_total)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular text-blue-900">{fmt(r.cet_total)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular text-emerald-900 font-bold">{fmt(r.accepted)}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mr-1.5 ${lgaLower ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                              {lgaLower ? "LGA eligible" : "NSWRA CET eligible"}
                            </span>
                            <span className="text-[10.5px] text-slate-700">{r.bl_note}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-1.5 text-[10px] text-slate-600 bg-amber-50/40 border-t border-amber-200">
                <span className="font-semibold text-amber-800">Note:</span> The headline accepted basis (top of panel) is the <em>aggregate</em> lower-of decision. Per-row notes below reflect each DM&#39;s <em>own</em> lower-of comparison — at this granularity some DMs may flip the other way without changing the aggregate basis. Form 2 column BL stamps the per-DM decision.
              </div>
              <div className="px-3 py-1.5 text-[10px] text-slate-500 bg-slate-50 border-t border-slate-200">
                {preview.rows?.length || 0} DM rows · BL70 carries the consolidated assessor narrative
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
// RFI Response Panel — assessor uploads RFI response + reviews diff
// ============================================================

export function RFIResponseButton({ job, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="rfi-response-pill"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-300 bg-purple-50 text-purple-800 text-[11px] tracking-wider uppercase font-bold hover:bg-purple-100"
      >
        <Upload className="w-3.5 h-3.5" /> + Add RFI response
        {(job?.rfi_chain || []).length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-purple-200 text-purple-900 text-[10px] font-semibold">{(job.rfi_chain || []).length}</span>
        )}
      </button>
      {open && <RFIResponseDrawer job={job} onClose={() => { setOpen(false); onChange && onChange(); }} />}
    </>
  );
}


function RFIResponseDrawer({ job, onClose }) {
  const [file, setFile] = useState(null);
  const [driveLink, setDriveLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [chain, setChain] = useState(job?.rfi_chain || []);
  const [decisionBusy, setDecisionBusy] = useState({});  // `${n}:${ref}` → bool
  const fileInputRef = useRef(null);

  const reloadChain = async () => {
    try {
      const r = await axios.get(`${API}/ingest/jobs/${job.id}/rfi-diff`);
      setChain(r.data?.rfi_chain || []);
    } catch (e) {
      // best-effort
    }
  };

  useEffect(() => { reloadChain(); }, []);

  const upload = async () => {
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      else if (driveLink) fd.append("drive_link", driveLink);
      else { setError("Pick a file or paste a Drive link."); setBusy(false); return; }
      const r = await axios.post(`${API}/ingest/jobs/${job.id}/rfi-response`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setChain(prev => [...prev, r.data.rfi_entry]);
      setFile(null); setDriveLink("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const setDecision = async (n, ref, decision, reason = "") => {
    const key = `${n}:${ref}`;
    setDecisionBusy(b => ({ ...b, [key]: true }));
    try {
      await axios.post(`${API}/ingest/jobs/${job.id}/rfi-decision`, { n, ref, decision, reason });
      await reloadChain();
    } finally {
      setDecisionBusy(b => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="rfi-response-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-purple-700">RFI response chain</div>
            <div className="font-heading text-lg font-semibold text-slate-800">{job?.top_dir_display || job?.top_dir}</div>
          </div>
          <button type="button" className="text-slate-500 hover:text-slate-800" onClick={onClose} data-testid="rfi-drawer-close"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Upload widget */}
          <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/40">
            <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-purple-700 mb-2">Upload new RFI response</div>
            <div className="text-sm text-slate-700 mb-3">
              Drop the council&apos;s updated CET workbook, RFI response ZIP, or paste a Drive link. We&apos;ll auto-diff each updated rate against the original lodgement so you can Accept / RFI / Reject row-by-row.
            </div>
            <input
              type="file"
              ref={fileInputRef}
              data-testid="rfi-file-input"
              accept=".zip,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm mb-2"
            />
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
              <span className="border-t border-slate-200 flex-1" />
              <span>or</span>
              <span className="border-t border-slate-200 flex-1" />
            </div>
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-slate-400" />
              <input
                type="url"
                data-testid="rfi-drive-link"
                placeholder="https://drive.google.com/... (RFI response file or zip)"
                value={driveLink}
                onChange={(e) => setDriveLink(e.target.value)}
                className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm"
              />
            </div>
            {error && <div className="mt-2 text-[12px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/> {error}</div>}
            <button
              type="button"
              data-testid="rfi-upload-btn"
              disabled={busy || (!file && !driveLink)}
              onClick={upload}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
              Upload &amp; auto-diff
            </button>
          </div>

          {/* Existing chain */}
          {chain.length === 0 && (
            <div className="text-sm text-slate-500 italic">No RFI responses uploaded yet for this submission.</div>
          )}
          {chain.map((entry) => (
            <RFIChainEntry key={entry.n} entry={entry} busyMap={decisionBusy} onDecision={setDecision} />
          ))}
        </div>
      </div>
    </div>
  );
}


function RFIChainEntry({ entry, busyMap, onDecision }) {
  const diffs = entry.diffs || [];
  const decisions = entry.decisions || {};
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`rfi-entry-${entry.n}`}>
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-[10.5px] tracking-[0.16em] uppercase font-bold text-slate-600">RFI response #{entry.n}</div>
          <div className="text-xs text-slate-500">{entry.uploaded_at && new Date(entry.uploaded_at).toLocaleString()}{entry.uploaded_by ? ` · ${entry.uploaded_by}` : ""}</div>
        </div>
        <div className="text-[11px] text-slate-500">{(entry.files || []).length} files · {diffs.length} workbook diff{diffs.length === 1 ? "" : "s"}</div>
      </div>
      {diffs.length === 0 && (
        <div className="p-4 text-sm text-slate-500 italic">No CET workbooks in this RFI response — files saved for the record.</div>
      )}
      {diffs.map((d, i) => (
        <div key={i} className="border-t border-slate-100">
          <div className="px-4 py-2 bg-amber-50/40 text-xs text-amber-900">
            <span className="font-semibold">Updated:</span> {d.updated_file}
            {d.original_file && <> &nbsp;<span className="text-slate-500">vs</span>&nbsp;<span className="font-semibold">Original:</span> {d.original_file}</>}
          </div>
          {d.error && <div className="px-4 py-3 text-sm text-red-600">{d.error}</div>}
          {Array.isArray(d.rows) && (
            <table className="w-full text-[12.5px]" data-testid={`rfi-diff-table-${entry.n}`}>
              <thead>
                <tr className="bg-white text-left text-[10.5px] tracking-[0.14em] uppercase text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-1.5">Ref</th>
                  <th className="px-3 py-1.5">Treatment</th>
                  <th className="px-3 py-1.5 text-right">Original</th>
                  <th className="px-3 py-1.5 text-right">Updated</th>
                  <th className="px-3 py-1.5 text-right">Δ</th>
                  <th className="px-3 py-1.5">Decision</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.filter(r => r.kind !== "unchanged").map((r) => {
                  const dec = decisions[r.ref]?.decision;
                  const key = `${entry.n}:${r.ref}`;
                  const busy = busyMap[key];
                  return (
                    <tr key={r.ref} className={`border-b border-slate-100 ${r.kind === "new" ? "bg-emerald-50/40" : r.kind === "removed" ? "bg-red-50/40" : "bg-amber-50/30"}`} data-testid={`rfi-diff-row-${entry.n}-${r.ref}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.ref}</td>
                      <td className="px-3 py-1.5">{r.treatment}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11.5px] text-slate-600">{r.original ? `${r.original.qty ?? "—"} × $${(r.original.rate ?? 0).toLocaleString()}` : <span className="text-emerald-700 font-semibold">new</span>}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11.5px] text-slate-800">{r.updated ? `${r.updated.qty ?? "—"} × $${(r.updated.rate ?? 0).toLocaleString()}` : <span className="text-red-700 font-semibold">removed</span>}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11px] tabular">{r.rate_delta_pct == null ? "—" : `${r.rate_delta_pct > 0 ? "+" : ""}${r.rate_delta_pct}%`}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex gap-1">
                          {["accept", "rfi", "reject"].map(verb => (
                            <button
                              key={verb}
                              type="button"
                              disabled={busy}
                              data-testid={`rfi-decision-${entry.n}-${r.ref}-${verb}`}
                              onClick={() => onDecision(entry.n, r.ref, verb)}
                              className={`px-2 py-0.5 rounded text-[10.5px] uppercase tracking-wider font-bold border ${
                                dec === verb
                                  ? (verb === "accept" ? "bg-emerald-600 text-white border-emerald-600"
                                     : verb === "rfi" ? "bg-amber-500 text-white border-amber-500"
                                     : "bg-red-600 text-white border-red-600")
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                              }`}
                            >{verb === "rfi" ? "RFI" : verb.charAt(0).toUpperCase() + verb.slice(1)}</button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// Evidentiary CET Panel — assessor reviews the rebuilt clean CET
// ============================================================

export function EvidentiaryCETButton({ job }) {
  const [open, setOpen] = useState(false);
  const submissionId = job?.submission_id;
  if (!submissionId) return null;
  return (
    <>
      <button
        type="button"
        data-testid="evidentiary-cet-pill"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-300 bg-blue-50 text-blue-800 text-[11px] tracking-wider uppercase font-bold hover:bg-blue-100"
      >
        <FileSpreadsheet className="w-3.5 h-3.5" /> Evidentiary CET
        {job?.evidentiary_cet && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 ml-0.5" />}
      </button>
      {open && <EvidentiaryCETDrawer submissionId={submissionId} job={job} onClose={() => setOpen(false)} />}
    </>
  );
}


function EvidentiaryCETDrawer({ submissionId, job, onClose }) {
  const [seed, setSeed] = useState(null);
  const [lga, setLga] = useState("");
  const [fy, setFy] = useState("FY25/26");
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [filter, setFilter] = useState("all"); // populated | all — default "all" so the catalogue is discoverable on first open
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/submissions/${submissionId}/draft-cet/seed`);
        setSeed(r.data);
        setLga(r.data.lga || "");
        setFy(r.data.fy || "FY25/26");
        setRows(r.data.rows || []);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load seed data");
      }
    })();
  }, [submissionId]);

  const setRow = (ref, key, value) => {
    setRows(rs => rs.map(r => r.ref === ref ? { ...r, [key]: value === "" ? null : Number(value) } : r));
  };

  const populated = rows.filter(r => r.qty != null && Number(r.qty) > 0);
  const visible = filter === "populated" ? populated : rows;

  const computePreview = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/submissions/${submissionId}/draft-cet`, {
        lga, fy,
        treatments: populated.map(r => ({ ref: r.ref, qty: r.qty, rate: r.rate })),
      });
      setPreview(r.data.preview);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadDraft = async () => {
    try {
      const resp = await axios.post(
        `${API}/submissions/${submissionId}/draft-cet/download`,
        { lga, fy, treatments: populated.map(r => ({ ref: r.ref, qty: r.qty, rate: r.rate })) },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Evidentiary_CET_v2.1.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Download failed");
    }
  };

  const publish = async () => {
    if (!window.confirm("Publish this Evidentiary CET? The headline P50 total will be written to the submission value and the workbook will be stamped into the Verified Package zip. You can re-publish later.")) return;
    setPublishing(true); setError("");
    try {
      const r = await axios.post(`${API}/submissions/${submissionId}/draft-cet/publish`, {
        lga, fy,
        treatments: populated.map(r => ({ ref: r.ref, qty: r.qty, rate: r.rate })),
      });
      alert(`Published. Submission value updated to ${fmtAUD(r.data.submission_value)}`);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  // Auto-preview whenever rows / lga / fy change (debounced)
  useEffect(() => {
    if (!seed) return;
    const t = setTimeout(() => computePreview(), 350);
    return () => clearTimeout(t);
  }, [rows, lga, fy, seed]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="evidentiary-cet-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-5xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-blue-700">Evidentiary CET (NSWRA v2.1 blank template)</div>
            <div className="font-heading text-lg font-semibold text-slate-800">{job?.top_dir_display || job?.top_dir}</div>
          </div>
          <button type="button" className="text-slate-500 hover:text-slate-800" onClick={onClose} data-testid="evidentiary-cet-close"><X className="w-5 h-5"/></button>
        </div>

        {!seed && !error && <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading seed data…</div>}
        {error && <div className="p-4 m-5 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

        {seed && (
          <div className="p-5 space-y-5">
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/40">
              <div className="text-sm text-slate-700 leading-relaxed">
                This rebuilds the council&apos;s cost estimate inside the <strong>unmodified official NSWRA Cost Estimation Tool v2.1</strong> template. The headline P50 total flows through the template&apos;s locked formulas (Qty&apos;s Estimate Sheet → Projected Escalation P50 → Summary Tab) — guaranteeing the same total NSWRA would compute had the council submitted a conformant CET.
              </div>
              {seed.sources && seed.sources.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-2">
                  Source rows extracted from: <span className="font-mono">{seed.sources.length} council CET workbook{seed.sources.length === 1 ? "" : "s"}</span>
                  {Object.keys(seed.rfi_decisions || {}).length > 0 && (
                    <> · <span className="text-purple-700">{Object.values(seed.rfi_decisions || {}).filter(v => v === "accept").length} RFI override{Object.values(seed.rfi_decisions || {}).filter(v => v === "accept").length === 1 ? "" : "s"} accepted</span></>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">Local Government Area</div>
                <input
                  data-testid="evidentiary-cet-lga"
                  value={lga} onChange={e => setLga(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
                  placeholder="e.g. Dungog Shire Council"
                />
              </div>
              <div>
                <div className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">Project Commencement FY</div>
                <select
                  data-testid="evidentiary-cet-fy"
                  value={fy} onChange={e => setFy(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
                >
                  {["FY24/25", "FY25/26", "FY26/27", "FY27/28"].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-slate-500">Treatments ({populated.length} populated · {rows.length} total)</div>
              <div className="flex gap-1 text-[10.5px] uppercase tracking-wider">
                <button type="button" onClick={() => setFilter("populated")} className={`px-2 py-0.5 rounded ${filter === "populated" ? "bg-blue-100 text-blue-800 font-semibold" : "text-slate-500"}`}>Populated only</button>
                <button type="button" onClick={() => setFilter("all")} className={`px-2 py-0.5 rounded ${filter === "all" ? "bg-blue-100 text-blue-800 font-semibold" : "text-slate-500"}`}>All ({rows.length})</button>
              </div>
            </div>

            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-[12.5px]" data-testid="evidentiary-cet-table">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10.5px] tracking-[0.14em] uppercase text-slate-500 border-b border-slate-200">
                    <th className="px-3 py-1.5">Section</th>
                    <th className="px-3 py-1.5">Ref</th>
                    <th className="px-3 py-1.5">Treatment</th>
                    <th className="px-3 py-1.5">Unit</th>
                    <th className="px-3 py-1.5 text-right">Quantity</th>
                    <th className="px-3 py-1.5 text-right">Rate ($)</th>
                    <th className="px-3 py-1.5 text-right">Direct Total</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => {
                    const direct = (Number(r.qty) || 0) * (Number(r.rate) || 0);
                    return (
                      <tr key={r.ref} className="border-b border-slate-100" data-testid={`evidentiary-cet-row-${r.ref}`}>
                        <td className="px-3 py-1 text-[10.5px] text-slate-500 uppercase tracking-wider">{r.section}</td>
                        <td className="px-3 py-1 font-mono text-[11px]">{r.ref}{r.rfi_decision === "accept" && <span className="ml-1 px-1 rounded bg-purple-100 text-purple-700 text-[9px]">RFI ✓</span>}</td>
                        <td className="px-3 py-1">{r.treatment_name}</td>
                        <td className="px-3 py-1 text-slate-600">{r.unit}</td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="any" value={r.qty ?? ""} onChange={e => setRow(r.ref, "qty", e.target.value)}
                                 className="w-24 border border-slate-200 rounded px-1.5 py-0.5 text-right text-[12px] font-mono"
                                 data-testid={`evidentiary-cet-qty-${r.ref}`}/>
                        </td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="any" value={r.rate ?? ""} onChange={e => setRow(r.ref, "rate", e.target.value)}
                                 className="w-24 border border-slate-200 rounded px-1.5 py-0.5 text-right text-[12px] font-mono"
                                 data-testid={`evidentiary-cet-rate-${r.ref}`}/>
                        </td>
                        <td className="px-3 py-1 text-right font-mono text-[11.5px] text-slate-600">{direct > 0 ? fmtAUD(direct) : "—"}</td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr><td colSpan="7" className="px-3 py-4 text-sm text-slate-400 text-center">No rows in this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Preview totals */}
            {preview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-slate-200 pt-4" data-testid="evidentiary-cet-preview">
                <PreviewStat label="Direct" value={preview.direct} />
                <PreviewStat label="PM + Design" value={preview.pm + preview.design} />
                <PreviewStat label="Contingency P50" value={preview.contingency_p50} />
                <PreviewStat label="Base + Cont. P50" value={preview.base_total} highlight />
                <PreviewStat label="Escalated P50 (headline)" value={preview.escalated_p50} highlight wide />
                {preview.rows_missing_rate > 0 && (
                  <div className="col-span-2 md:col-span-3 text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/> {preview.rows_missing_rate} row{preview.rows_missing_rate === 1 ? "" : "s"} have quantity but no rate — please enter rates.</div>
                )}
              </div>
            )}

            <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2 sticky bottom-0 bg-white pb-4">
              <button type="button" onClick={downloadDraft} data-testid="evidentiary-cet-download-btn"
                      className="btn btn-outline whitespace-nowrap text-sm">Download .xlsx for desk review</button>
              <button type="button" onClick={publish} disabled={publishing || populated.length === 0}
                      data-testid="evidentiary-cet-publish-btn"
                      className="btn btn-primary whitespace-nowrap text-sm">
                {publishing ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                Approve &amp; publish to Form 2
              </button>
              <div className="ml-auto text-[11px] text-slate-500 self-end">{populated.length} treatment{populated.length === 1 ? "" : "s"} ready · {preview ? fmtAUD(preview.escalated_p50) : "—"}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function PreviewStat({ label, value, highlight = false, wide = false }) {
  return (
    <div className={`p-2 rounded border ${highlight ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"} ${wide ? "col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">{label}</div>
      <div className="font-mono text-base tabular text-slate-800">{fmtAUD(value)}</div>
    </div>
  );
}
